import React from 'react';
import { Modal } from 'react-bootstrap';
import Button from '../button';
import { useLocation, useNavigate } from 'react-router-dom';
import { setParamsInURL } from '../../../utils/helper';
import { orderSummary, attribute } from '../../../utils/router/constant';
import './index.scss';

const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  const date = new Date(dateStr);
  if (isNaN(date)) return dateStr;
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric"
  });
};

const OrderRestrictionModal = ({ show, onHide, errorData, entityid, orderId, productName }) => {
  const location = useLocation();
  const nav = useNavigate();

  if (!errorData) return null;

  const { error_code } = errorData;
  const payload = errorData.data || {}; // assuming the data payload is inside 'data'

  const handlePrimaryAction = async () => {
    if (error_code === 'ERR_029' || error_code === 'ERR_030') {
      const paramObject = { entityid: entityid, orderId: orderId };
      const params = await setParamsInURL(location, paramObject);
      nav(`${attribute}?${params}`);
      return;
    }

    const oid = payload?.orderEntityId || payload?.orderId || payload?.entityid || errorData?.entityid || orderId;
    const paramObject = { entityid: entityid, orderId: oid };
    const params = await setParamsInURL(location, paramObject);
    nav(`${orderSummary}?${params}`);
  };

  let title, message, boxTitle, badgeText, badgeClass, primaryBtnText, secondaryBtnText;

  switch (error_code) {
    case 'ERR_022':
      title = "Personalized Plan Ready";
      message = "We've already created a personalized plan for you with our advisor. Please continue your purchase here.";
      boxTitle = "Existing Pending Order";
      badgeText = "Pending";
      badgeClass = "badge-pending";
      primaryBtnText = "Continue with advised plan";
      secondaryBtnText = "Go Back";
      break;
    case 'ERR_023':
      title = "Active Policy Found";
      message = "We see you already have an active policy. Would you like to renew it instead of creating a new one?";
      boxTitle = "Existing Active Policy";
      badgeText = "Active";
      badgeClass = "badge-active";
      primaryBtnText = "Renew Policy";
      secondaryBtnText = "Cancel";
      break;
    case 'ERR_024':
      title = "Grace Period Lapsed";
      message = "Your policy's grace period has expired. You can no longer renew. Please create a fresh order.";
      boxTitle = "Policy Details";
      badgeText = "Lapsed";
      badgeClass = "badge-lapsed";
      primaryBtnText = null; // Only cancel button
      secondaryBtnText = "Cancel";
      break;
    case 'ERR_025':
      title = "Policy in Grace Period";
      message = "Your previous policy has recently expired but is still within the grace period, which allows you to renew it without losing benefits.";
      boxTitle = "Policy Details";
      badgeText = `Expiring in ${payload.daysRemaining || 0} days`;
      badgeClass = "badge-expiring";
      primaryBtnText = "Renew Policy";
      secondaryBtnText = "Cancel";
      break;
    case 'ERR_029':
      title = "Update Required";
      message = "Kindly update your attribute details";
      boxTitle = "Renewal Order";
      badgeText = "Action Needed";
      badgeClass = "badge-pending";
      primaryBtnText = "Update Details";
      secondaryBtnText = "Cancel";
      break;
    case 'ERR_030':
      title = "Update Required";
      message = "Selected category does not match with your qualification";
      boxTitle = "Renewal Order";
      badgeText = "Action Needed";
      badgeClass = "badge-pending";
      primaryBtnText = "Update Details";
      secondaryBtnText = "Cancel";
      break;
    default:
      return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered className="order-restriction-modal" backdrop="static">
      <Modal.Header closeButton className="border-0 pb-0"></Modal.Header>
      <Modal.Body className="pt-0">
        <h4 className="modal-main-title">{title}</h4>
        <p className="modal-main-subtitle">{message}</p>

        <div className="details-box">
          <div className="details-box-header">
            <span className="box-title-text"><i className="bi bi-file-earmark-text me-2"></i> {boxTitle}</span>
            <span className={`custom-badge ${badgeClass}`}>
              {error_code === 'ERR_022' && <i className="bi bi-clock me-1"></i>}
              {error_code === 'ERR_025' && <i className="bi bi-clock me-1"></i>}
              {badgeText}
            </span>
          </div>

          <div className="details-box-body">
            {error_code === 'ERR_022' ? (
              <>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value link-color">#{payload?.code || errorData?.code}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Order Date</span>
                    <span className="detail-value">{formatDate(payload?.created_date || errorData?.created_date)}</span>
                  </div>
                </div>
                <div className="detail-row mt-3">
                  <div className="detail-item">
                    <span className="detail-label">Product</span>
                    <span className="detail-value">{payload?.name || errorData?.name}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Order Type</span>
                    <span className="detail-value">{payload?.order_type || errorData?.order_type || 'Fresh Order'}</span>
                  </div>
                </div>
                <hr className="detail-divider" />
                <div className="detail-row">
                  <div className="detail-item full-width">
                    <span className="detail-label">Relationship Manager</span>
                    <span className="detail-value">{`${payload?.billing_first_name || errorData?.billing_first_name || ''} ${payload?.billing_last_name || errorData?.billing_last_name || ''}`.trim() || 'N/A'}</span>
                  </div>
                </div>
              </>
            ) : error_code === 'ERR_023' ? (
              <>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value link-color">#{payload.policyId || payload.policyNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Expiry Date</span>
                    <span className="detail-value">{formatDate(payload.expiryDate || payload.expiredOn)}</span>
                  </div>
                </div>
                <div className="detail-row mt-3">
                  <div className="detail-item">
                    <span className="detail-label">Product</span>
                    <span className="detail-value">{payload.product}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Status</span>
                    <span className="detail-value">Active</span>
                  </div>
                </div>
              </>
            ) : error_code === 'ERR_024' || error_code === 'ERR_025' ? (
              // ERR_024 and ERR_025
              <>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Policy Number</span>
                    <span className="detail-value link-color">#{payload.policyId || payload.policyNumber}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Expired On</span>
                    <span className="detail-value">{formatDate(payload.expiredOn)}</span>
                  </div>
                </div>
                <div className="detail-row mt-3">
                  <div className="detail-item">
                    <span className="detail-label">Product</span>
                    <span className="detail-value">{payload.product}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">{error_code === 'ERR_024' ? 'Grace Period Ended' : 'Grace Period Ends'}</span>
                    <span className="detail-value">{formatDate(payload.gracePeriodEnds)}</span>
                  </div>
                </div>
              </>
            ) : error_code === 'ERR_029' || error_code === 'ERR_030' ? (
              <>
                <div className="detail-row">
                  <div className="detail-item">
                    <span className="detail-label">Order ID</span>
                    <span className="detail-value link-color">#{orderId}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Product</span>
                    <span className="detail-value">{productName || payload?.name || errorData?.name}</span>
                  </div>
                </div>
              </>
            ) : null}
          </div>
        </div>

        <div className="modal-actions mt-4">
          {primaryBtnText && (
            <Button type="button" variant="primary" className="w-100 mb-3" onClick={handlePrimaryAction} label={primaryBtnText} />
          )}
          <button type="button" className="btn-secondary-link" onClick={onHide}>
            {secondaryBtnText}
          </button>
        </div>
      </Modal.Body>
    </Modal>
  );
};

export default OrderRestrictionModal;
